<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useMapGetter } from 'dashboard/composables/store';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import DuplicadosAPI from 'dashboard/api/fandata/duplicados';

// T-118 — merge assistido. O sistema aponta os grupos; quem decide é gente.
// A assimetria entre as duas ações é de propósito: descartar é reversível e vai em massa,
// mesclar é irreversível e vai um grupo por vez, com a base escolhida na tela.
const { t } = useI18n();
const currentAccountId = useMapGetter('getCurrentAccountId');

const isFetching = ref(true);
const grupos = ref([]);
const ignorados = ref(0);
const selecionados = ref([]);
const basePorGrupo = ref({});
const mesclando = ref(null);
const descartando = ref(false);
const showMesclar = ref(false);
const grupoParaMesclar = ref(null);

const temSelecao = computed(() => selecionados.value.length > 0);

const aplicar = data => {
  grupos.value = data.grupos || [];
  if (data.ignorados !== undefined) ignorados.value = data.ignorados;
  selecionados.value = [];
  // Base padrão: o contato com mais conversas. É o que costuma ter o histórico mais completo,
  // e é reversível na tela antes de confirmar.
  const bases = {};
  grupos.value.forEach(g => {
    const maior = [...g.contatos].sort((a, b) => b.conversas - a.conversas)[0];
    bases[g.digest] = maior?.id;
  });
  basePorGrupo.value = bases;
};

const carregar = async () => {
  isFetching.value = true;
  try {
    const { data } = await DuplicadosAPI.get();
    aplicar(data);
  } catch (error) {
    useAlert(t('DUPLICADOS.ERRO_CARREGAR'));
  } finally {
    isFetching.value = false;
  }
};

const alternar = digest => {
  const i = selecionados.value.indexOf(digest);
  if (i >= 0) selecionados.value.splice(i, 1);
  else selecionados.value.push(digest);
};

const descartarSelecionados = async () => {
  descartando.value = true;
  try {
    const { data } = await DuplicadosAPI.ignorar(selecionados.value);
    aplicar(data);
    useAlert(t('DUPLICADOS.DESCARTADOS'));
  } catch (error) {
    useAlert(t('DUPLICADOS.ERRO'));
  } finally {
    descartando.value = false;
  }
};

const pedirMerge = grupo => {
  grupoParaMesclar.value = grupo;
  showMesclar.value = true;
};

const fecharMerge = () => {
  showMesclar.value = false;
  grupoParaMesclar.value = null;
};

const mesclar = async () => {
  const grupo = grupoParaMesclar.value;
  if (!grupo) return;
  const baseId = basePorGrupo.value[grupo.digest];
  const outros = grupo.contatos.filter(c => c.id !== baseId).map(c => c.id);
  mesclando.value = grupo.digest;
  try {
    const { data } = await DuplicadosAPI.mesclar(baseId, outros);
    aplicar(data);
    useAlert(t('DUPLICADOS.MESCLADO', { quantos: outros.length }));
  } catch (error) {
    useAlert(t('DUPLICADOS.ERRO'));
  } finally {
    mesclando.value = null;
    fecharMerge();
  }
};

// Sem digests = devolve todos os descartados para a lista. É o desfazer do botão de massa.
const reconsiderarTodos = async () => {
  try {
    const { data } = await DuplicadosAPI.reconsiderar();
    aplicar(data);
  } catch (error) {
    useAlert(t('DUPLICADOS.ERRO'));
  }
};

const baseDoGrupo = grupo =>
  grupo.contatos.find(c => c.id === basePorGrupo.value[grupo.digest]);

const nomeDaBase = grupo => baseDoGrupo(grupo)?.nome || `#${basePorGrupo.value[grupo.digest]}`;

const urlConversa = displayId =>
  frontendURL(`accounts/${currentAccountId.value}/conversations/${displayId}`);

const urlContato = id =>
  frontendURL(`accounts/${currentAccountId.value}/contacts/${id}`);

const formatarData = valor => {
  if (!valor) return '';
  return new Date(valor).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('DUPLICADOS.HEADER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('DUPLICADOS.HEADER')"
        :description="$t('DUPLICADOS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4 mt-4">
        <!-- Barra de ação em massa: só descarte, que é reversível -->
        <div
          v-if="grupos.length"
          class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="text-sm text-n-slate-11">
            {{
              temSelecao
                ? $t('DUPLICADOS.SELECIONADOS', {
                    quantos: selecionados.length,
                  })
                : $t('DUPLICADOS.TOTAL', { quantos: grupos.length })
            }}
          </span>
          <div class="flex items-center gap-3">
            <button
              v-if="ignorados > 0"
              type="button"
              class="text-sm text-n-slate-11 hover:text-n-slate-12 hover:underline"
              @click="reconsiderarTodos"
            >
              {{ $t('DUPLICADOS.VER_DESCARTADOS', { quantos: ignorados }) }}
            </button>
            <Button
              :label="$t('DUPLICADOS.DESCARTAR_SELECIONADOS')"
              variant="faded"
              color="slate"
              size="sm"
              :disabled="!temSelecao"
              :is-loading="descartando"
              @click="descartarSelecionados"
            />
          </div>
        </div>

        <div
          v-if="!grupos.length"
          class="flex flex-col items-center gap-2 px-5 py-16 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="i-lucide-check-check size-6 text-n-teal-11" />
          <p class="text-sm text-n-slate-11">{{ $t('DUPLICADOS.VAZIO') }}</p>
          <button
            v-if="ignorados > 0"
            type="button"
            class="text-sm text-n-brand hover:underline"
            @click="reconsiderarTodos"
          >
            {{ $t('DUPLICADOS.VER_DESCARTADOS', { quantos: ignorados }) }}
          </button>
        </div>

        <!-- Um bloco por grupo, contatos lado a lado -->
        <section
          v-for="grupo in grupos"
          :key="grupo.digest"
          class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                :checked="selecionados.includes(grupo.digest)"
                @change="alternar(grupo.digest)"
              />
              <span class="flex flex-col">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    $t('DUPLICADOS.GRUPO', {
                      quantos: grupo.contatos.length,
                      rotulo: grupo.rotulo,
                    })
                  }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ $t('DUPLICADOS.GRUPO_HINT') }}
                </span>
              </span>
            </label>
            <Button
              :label="$t('DUPLICADOS.MESCLAR')"
              icon="i-lucide-merge"
              size="sm"
              class="flex-shrink-0"
              :is-loading="mesclando === grupo.digest"
              @click="pedirMerge(grupo)"
            />
          </div>

          <div
            class="grid gap-4"
            :class="
              grupo.contatos.length > 2
                ? 'grid-cols-1 md:grid-cols-3'
                : 'grid-cols-1 md:grid-cols-2'
            "
          >
            <div
              v-for="contato in grupo.contatos"
              :key="contato.id"
              class="flex flex-col gap-2 p-4 border rounded-lg"
              :class="
                basePorGrupo[grupo.digest] === contato.id
                  ? 'border-n-brand bg-n-alpha-1'
                  : 'border-n-weak'
              "
            >
              <label class="flex items-start gap-2 cursor-pointer">
                <input
                  v-model="basePorGrupo[grupo.digest]"
                  type="radio"
                  :value="contato.id"
                  class="mt-1"
                />
                <span class="flex flex-col">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ contato.nome || $t('DUPLICADOS.SEM_NOME') }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ $t('DUPLICADOS.MANTER') }}
                  </span>
                </span>
              </label>

              <dl class="flex flex-col gap-1 text-sm">
                <div
                  v-for="campo in [
                    { chave: 'CPF', valor: contato.cpf },
                    { chave: 'E-mail', valor: contato.email },
                    { chave: 'Telefone', valor: contato.telefone },
                  ]"
                  :key="campo.chave"
                  class="flex gap-2"
                >
                  <dt class="flex-shrink-0 text-n-slate-11">
                    {{ campo.chave }}
                  </dt>
                  <dd class="truncate text-n-slate-12">
                    {{ campo.valor || $t('DUPLICADOS.NAO_INFORMADO') }}
                  </dd>
                </div>
              </dl>

              <div class="flex flex-col gap-1 pt-2 border-t border-n-weak">
                <span class="text-xs text-n-slate-11">
                  {{
                    $t('DUPLICADOS.CONVERSAS', {
                      quantas: contato.conversas,
                      desde: formatarData(contato.criado_em),
                    })
                  }}
                </span>
                <a
                  v-for="conversa in contato.ultimas"
                  :key="conversa.display_id"
                  :href="urlConversa(conversa.display_id)"
                  target="_blank"
                  rel="noopener"
                  class="text-xs text-n-brand hover:underline"
                >
                  #{{ conversa.display_id }} · {{ conversa.status }} ·
                  {{ formatarData(conversa.em) }}
                </a>
                <a
                  :href="urlContato(contato.id)"
                  target="_blank"
                  rel="noopener"
                  class="mt-1 text-xs text-n-slate-11 hover:underline"
                >
                  {{ $t('DUPLICADOS.ABRIR_CONTATO') }}
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </template>

    <woot-delete-modal
      v-model:show="showMesclar"
      :on-close="fecharMerge"
      :on-confirm="mesclar"
      :title="$t('DUPLICADOS.CONFIRMA')"
      :message="
        grupoParaMesclar
          ? $t('DUPLICADOS.CONFIRMA_HINT', {
              quantos: grupoParaMesclar.contatos.length - 1,
              base: nomeDaBase(grupoParaMesclar),
            })
          : ''
      "
      :confirm-text="$t('DUPLICADOS.MESCLAR')"
      :reject-text="$t('DUPLICADOS.CANCELAR')"
    />
  </SettingsLayout>
</template>
